import React from "react";
import { get } from "lodash";
import moment from "moment";
import { message } from "antd";
import { isPdf } from "@/public/utils";

// 解析文件/图片字段值：后端返回可能是 JSON 字符串、纯 URL 或逗号拼接的多 URL，统一转成数组
export var parseFileFieldValue = function (val) {
  if (typeof val === "string" && val) {
    try {
      var parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      // JSON.parse 成功但不是数组（如纯数字、对象），按单个值处理
      return [String(parsed)];
    } catch (e) {
      // 非 JSON 字符串（如纯 URL 或逗号拼接的多 URL），按逗号拆分
      return val.split(",").filter(Boolean);
    }
  }
  return Array.isArray(val) ? val : [];
};

// 把 URL 数组转成 Upload 组件需要的 fileList 格式
export var buildFileList = function (urls) {
  return (urls || []).map(function (url, idx) {
    return {
      uid: "" + idx,
      name: url,
      response: { url: url },
      status: "done",
      url: url,
    };
  });
};

// 把 URL 数组转成 Picture 用的 imgList 格式（uid 用负数避免和 fileList 冲突）
export var buildImgList = function (urls) {
  return (urls || []).map(function (url, idx) {
    return {
      uid: "-" + idx,
      name: url || "image.png",
      response: { url: url },
      status: "done",
      url: url,
    };
  });
};

// 根据 is_match_change 取字段当前值（数组或字符串）
export var getFieldRawValue = function (field) {
  return field.is_match_change
    ? field.change_after_field_name
    : field.qua_filed_value;
};

// 根据 is_match_change 写回字段值
export var setFieldRawValue = function (field, value) {
  if (field.is_match_change) {
    field.change_after_field_name = value;
  } else {
    field.qua_filed_value = value;
  }
};

// 查询数据转换：按 qua_name 分组，qua_value 为二维数组
export var transformQueryData = function (data) {
  var quaList = data.match_qua_list || [];
  var groupMap = {};
  quaList.forEach(function (qua) {
    var quaName = qua.qua_name || "未知资质";
    if (!groupMap[quaName]) {
      groupMap[quaName] = {
        qua_name: quaName,
        qua_code: qua.qua_code || "",
        is_need: qua.is_need ? 1 : 0,
        company_name: qua.company_name || "",
        company_code: qua.company_code || "",
        enterprise_name: qua.enterprise_name || "",
        enterprise_code: qua.enterprise_code || "",
        qua_value: [],
        _enterprise_qua_codes: [],
      };
    }
    var fieldArray = (qua.detail_list || []).map(function (d) {
      var field = Object.assign({}, d, {
        is_need: d.is_need ? 1 : 0,
        // 关键：把原始字符串解析成数组后写回字段，后续删除/上传都直接操作数组
        qua_filed_value: d.qua_filed_value || "",
        change_after_field_name: d.change_after_field_name || "",
      });
      if (!field.qua_name) field.qua_name = qua.qua_name || "";
      if (!field.qua_code) field.qua_code = qua.qua_code || "";
      if (!field.company_name) field.company_name = qua.company_name || "";
      if (!field.company_code) field.company_code = qua.company_code || "";
      if (!field.enterprise_name)
        field.enterprise_name = qua.enterprise_name || "";
      if (!field.enterprise_code)
        field.enterprise_code = qua.enterprise_code || "";

      // 解析文件/图片值，并把数组写回字段（修复删除附件后保存时值未清空的问题）
      // 仅文件/图片类型写回数组；Date/Text 等类型保持原始字符串（如日期 "2025-06-12"）
      var isMatchChange = !!d.is_match_change;
      var isFileType =
        d.qua_filed_type === "Picture" ||
        d.qua_filed_type === "File" ||
        d.qua_filed_type === "Picture&File";
      var rawValue = isMatchChange
        ? d.change_after_field_name
        : d.qua_filed_value;
      var parsedValue = parseFileFieldValue(rawValue);

      if (isFileType) {
        // 写回解析后的数组，保证删除/上传逻辑能正确操作数组
        if (isMatchChange) {
          field.change_after_field_name = parsedValue;
        } else {
          field.qua_filed_value = parsedValue;
        }
      }

      if (d.qua_filed_type === "Picture" && parsedValue.length > 0) {
        field.imgList = buildImgList(parsedValue);
      }
      if (
        (d.qua_filed_type === "File" ||
          d.qua_filed_type === "Picture&File") &&
        parsedValue.length > 0
      ) {
        field.fileListNew = buildFileList(parsedValue);
      }
      return field;
    });
    // 开始时间固定排在结束时间前面（后端 detail_list 顺序不保证）
    var orderMap = { 开始时间: 0, 结束时间: 1 };
    fieldArray.sort(function (a, b) {
      var orderA = orderMap[a.qua_filed_name] !== undefined
        ? orderMap[a.qua_filed_name]
        : 2;
      var orderB = orderMap[b.qua_filed_name] !== undefined
        ? orderMap[b.qua_filed_name]
        : 2;
      return orderA - orderB;
    });
    groupMap[quaName].qua_value.push(fieldArray);
    groupMap[quaName]._enterprise_qua_codes.push(qua.enterprise_qua_code || "");
  });
  return Object.values(groupMap);
};

// 保存数据转换：qua_value 二维数组 -> qualification_list 扁平数组
export var transformSaveData = function (qualificationData) {
  var saveData = [];
  qualificationData.forEach(function (quaGroup) {
    if (quaGroup && quaGroup.qua_value) {
      quaGroup.qua_value.forEach(function (instance, instIndex) {
        var quaItem = {
          qua_name: quaGroup.qua_name || "",
          qua_code: quaGroup.qua_code || "",
          is_need: quaGroup.is_need === 1,
          company_name: quaGroup.company_name || "",
          company_code: quaGroup.company_code || "",
          enterprise_name: quaGroup.enterprise_name || "",
          enterprise_code: quaGroup.enterprise_code || "",
          enterprise_qua_code:
            (quaGroup._enterprise_qua_codes &&
              quaGroup._enterprise_qua_codes[instIndex]) ||
            "",
          detail_list: instance.map(function (f) {
            return {
              id: f.id || null,
              qua_filed_name: f.qua_filed_name || "",
              // 图片/文件类型：数组转成逗号拼接字符串传给后端
              qua_filed_value: (function () {
                var rawVal = getFieldRawValue(f);
                return Array.isArray(rawVal) ? rawVal.join(",") : rawVal || "";
              })(),
              is_match_change: f.is_match_change || false,
              qua_filed_type: f.qua_filed_type || "Text",
              qua_code: f.qua_code || "",
              enterprise_qua_code: f.enterprise_qua_code || "",
              change_detail_id: f.change_detail_id || null,
              is_need: f.is_need === 1,
              business_code: f.business_code || "",
              business_name: f.business_name || "",
            };
          }),
        };
        saveData.push(quaItem);
      });
    }
  });
  return saveData;
};

// 找到 qualificationData 中指定 index/index2/name 的 field
var findField = function (data, index, index2, name) {
  return data[index].qua_value[index2].find(function (f) {
    return f.qua_filed_name === name;
  });
};

// 输入框
export var createHandleChangeInput = function (qualificationData, setQualificationData) {
  return function (e, index, index2, name) {
    var data = qualificationData.slice();
    var field = findField(data, index, index2, name);
    if (field) {
      setFieldRawValue(field, e.target.value);
      setQualificationData(data);
    }
  };
};

// 下拉框
export var createHandelSelect = function (qualificationData, setQualificationData) {
  return function (value, index, index2, name) {
    var data = qualificationData.slice();
    var field = findField(data, index, index2, name);
    if (field) {
      setFieldRawValue(field, value);
      setQualificationData(data);
    }
  };
};

// 时间
export var createHandleChangeDate = function (qualificationData, setQualificationData) {
  return function (e, index, index2, name) {
    var data = qualificationData.slice();
    var field = findField(data, index, index2, name);
    if (field) {
      var val = e ? moment(e).format("YYYY-MM-DD") : null;
      setFieldRawValue(field, val);
    }
    setQualificationData(data);
  };
};

// 图片上传
export var createHandleChangeResponsibility = function (qualificationData, setQualificationData) {
  return function (index, index2, name, info) {
    var fileList = info.fileList;
    var data = qualificationData.slice();
    var field = findField(data, index, index2, name);
    if (!field) return;
    field.imgList = fileList;
    if (info.file.status === "done") {
      var urls = [];
      fileList.forEach(function (item) {
        if (item.response && item.response.url) {
          urls.push(item.response.url);
        }
      });
      setFieldRawValue(field, urls);
    }
    setQualificationData(data);
  };
};

// 删除图片
export var createHandleDeleteImage = function (qualificationData, setQualificationData) {
  return function (e, index, index2, name, index3) {
    e.stopPropagation();
    var data = qualificationData.slice();
    var field = findField(data, index, index2, name);
    if (!field) return;
    var url = field.imgList[index3].response.url;
    var targetArr = getFieldRawValue(field);
    if (Array.isArray(targetArr)) {
      var filtered = targetArr.filter(function (item) {
        return item !== url;
      });
      setFieldRawValue(field, filtered);
    }
    field.imgList.splice(index3, 1);
    setQualificationData(data);
  };
};

// 文件上传
export var createHandleChangeFileAuthorize = function (qualificationData, setQualificationData) {
  return function (index, index2, name, info) {
    var fileList = info.fileList;
    var data = qualificationData.slice();
    var field = findField(data, index, index2, name);
    if (!field) return;
    field.fileListNew = fileList;
    if (info.file.status === "done") {
      var urls = [];
      fileList.forEach(function (item) {
        if (item.response && item.response.url) {
          urls.push(item.response.url);
        }
      });
      setFieldRawValue(field, urls);
    }
    setQualificationData(data);
  };
};

// 删除文件
export var createHandleDeleteFile = function (qualificationData, setQualificationData) {
  return function (index, index2, name, file) {
    var data = qualificationData.slice();
    var field = findField(data, index, index2, name);
    if (!field) return;
    var url = file.response.url;
    var targetArr = getFieldRawValue(field);
    if (Array.isArray(targetArr)) {
      var filtered = targetArr.filter(function (item) {
        return item !== url;
      });
      setFieldRawValue(field, filtered);
    }
    setQualificationData(data);
  };
};

// 预览
export var createOnPreview = function (setPreViewImgList, setPreviewVisible) {
  return function (file) {
    var url = file.url || get(file, "response.url");
    if (!isPdf(url)) {
      setPreViewImgList([{ src: url }]);
      setPreviewVisible(true);
    } else {
      window.open(url);
    }
  };
};

// 必填校验，返回 boolean
export var createCheckQualificationsData = function (qualificationData) {
  return function () {
    var errorMessage = "";
    for (var i = 0; i < qualificationData.length; i++) {
      var quaGroup = qualificationData[i];
      if (quaGroup && quaGroup.qua_value && quaGroup.is_need === 1) {
        for (var j = 0; j < quaGroup.qua_value.length; j++) {
          var instance = quaGroup.qua_value[j];
          for (var k = 0; k < instance.length; k++) {
            var field = instance[k];
            if (field.is_need) {
              var checkValue = getFieldRawValue(field);
              if (
                checkValue === "" ||
                checkValue === null ||
                (Array.isArray(checkValue) && checkValue.length === 0)
              ) {
                errorMessage =
                  quaGroup.qua_name +
                  " 第" +
                  (j + 1) +
                  '条资质的"' +
                  field.qua_filed_name +
                  '"为必填项';
                message.warning(errorMessage || "请选择必填项", 1.5);
                return false;
              }
            }
          }
          if (errorMessage) break;
        }
      }
      if (errorMessage) break;
    }
    return true;
  };
};

// 资质示例链接（营业执照/医疗器械经营许可证等）
export var getShow = function (name) {
  var dom = "";
  switch (name) {
    case "营业执照":
      dom = (
        <a
          href="https://static.vsigo.cn/static/supplier/yingye.png"
          target="_blank"
        >
          示例
        </a>
      );
      break;
    case "医疗器械经营许可证":
      dom = (
        <a
          href="https://static.vsigo.cn/static/supplier/yiliao.png"
          target="_blank"
        >
          示例
        </a>
      );
      break;
    case "开户许可证":
      dom = (
        <a
          href="https://static.vsigo.cn/static/supplier/kaihu.png"
          target="_blank"
        >
          示例
        </a>
      );
      break;
    case "医疗器械生产许可证及其产品登记表":
      dom = (
        <a
          href="https://static.vsigo.cn/static/supplier/yiliaoxuke.png"
          target="_blank"
        >
          示例
        </a>
      );
      break;
    default:
      break;
  }
  return dom;
};



// WEBPACK FOOTER //
// ./src/pages/newsuppliersettlement/supplierinformationentry/qualification-util.js